/**
 * usePartnerStatus Hook
 * Handles partner live status checking.
 * After a fresh login, partner and email status are already in Redux from the
 * enriched login response, so the initial API calls are skipped.
 */

import { useCallback, useEffect, useRef } from 'react';

import {
    checkIsPartnerLive,
    checkValidEmail,
    selectHasValidEmail,
    selectIsPartnerLive,
    selectIsPartnerLoading,
    selectPartner,
    selectPartnerDisabledData,
    selectPartnerError,
    selectShowUserDetailsForm,
} from '../store/slices';
import { PartnerDisabledData } from '../store/types';
import { useAppDispatch } from './use-app-dispatch';
import { useAppSelector } from './use-app-selector';

export interface UsePartnerStatusReturn {
    isPartnerLive: boolean;
    isLoading: boolean;
    hasValidEmail: boolean;
    showUserDetails: boolean;
    partnerDisabledData: PartnerDisabledData | null;
    checkPartnerAndEmail: () => Promise<void>;
    refreshPartnerStatus: () => Promise<void>;
    error: string | null;
}

export const usePartnerStatus = (): UsePartnerStatusReturn => {
    const dispatch = useAppDispatch();
    const hasCheckedRef = useRef(false);

    const partner = useAppSelector(selectPartner);
    const isPartnerLive = useAppSelector(selectIsPartnerLive);
    const partnerDisabledData = useAppSelector(selectPartnerDisabledData);
    const hasValidEmail = useAppSelector(selectHasValidEmail);
    const showUserDetails = useAppSelector(selectShowUserDetailsForm);
    const isLoading = useAppSelector(selectIsPartnerLoading);
    const error = useAppSelector(selectPartnerError);

    const refreshPartnerStatus = useCallback(async (): Promise<void> => {
        await dispatch(checkIsPartnerLive());
    }, [dispatch]);

    /**
     * Check partner status and then email validity via API.
     * Skipped when state is already populated from login.
     */
    const checkPartnerAndEmail = useCallback(async (): Promise<void> => {
        const partnerResult = await dispatch(checkIsPartnerLive()).unwrap();

        if (partnerResult.prod_allowed) {
            await dispatch(checkValidEmail());
        }
    }, [dispatch]);

    // Auto-check on mount. Skip if partner state was already populated by
    // the enriched login response (prodAllowed or disabledData already set).
    useEffect(() => {
        if (!hasCheckedRef.current) {
            hasCheckedRef.current = true;

            const alreadyResolved = partner.prodAllowed || partner.disabledData !== null;
            if (!alreadyResolved) {
                checkPartnerAndEmail().catch(() => {
                    // Error is captured in Redux via the rejected thunk
                });
            }
        }
    }, [checkPartnerAndEmail, partner.prodAllowed, partner.disabledData]);

    return {
        isPartnerLive,
        isLoading,
        hasValidEmail,
        showUserDetails,
        partnerDisabledData,
        checkPartnerAndEmail,
        refreshPartnerStatus,
        error,
    };
};

export default usePartnerStatus;
